import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFeatureNav } from '../../hooks/useFeatureNav';
import { Plus, X, Loader2, Check, Play, Lock, Clock, Trash2, BarChart3, Users, Share2, ShieldCheck, Copy, Search, Send } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { AaiRequest, AaiSkill, PersonalSkill, SkillAccessRow, SkillUsage, Graduation } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { PageHeader } from './dashboards/ui';
import RunSkillModal from './RunSkillModal';

const ACCESS_BADGE: Record<string, string> = {
  open: 'bg-emerald-50 text-emerald-700',
  request: 'bg-brand-50 text-brand-700',
  restricted: 'bg-amber-50 text-amber-700',
};
const inputCls = 'w-full rounded-lg border border-line px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200';

// The marketplace list API (`_skill()` serializer) returns `scope`/`kind`/
// `author_email` alongside the typed AaiSkill fields; the shared type doesn't
// name them, so we widen locally rather than edit the shared type.
type MarketSkill = AaiSkill & { scope?: string | null; kind?: string | null; author_email?: string | null };

const VET_BADGE: Record<'vetted' | 'review', string> = {
  vetted: 'bg-emerald-50 text-emerald-700',
  review: 'bg-amber-50 text-amber-700',
};

// Vet state is derived ONLY from fields this endpoint actually returns.
//  • `published`    → the listing cleared the publish checklist (name, problem,
//                     ≥1 step, both examples, an access level — enforced in
//                     set_published). Unpublished = still a draft (editor-only).
//  • `author_email` → the skill began as a participant's PERSONAL skill and
//                     reached the marketplace by GRADUATING, which requires the
//                     run-and-refine vetting loop (≥3 runs, ≥1 worked, ≥2
//                     versions) plus a champion's sign-off. Editor-authored
//                     company skills leave this null.
// There is no scan/prompt-injection field in the API, so we never print a
// "scan clean" verdict and there is no red "Blocked" state to fabricate.
type VetKey = 'vetted' | 'review';
function vetState(s: MarketSkill): { key: VetKey; label: string; basis: string } {
  if (!s.published) return { key: 'review', label: 'In review', basis: 'Draft — has not cleared the publish checklist yet.' };
  if (s.author_email) return { key: 'vetted', label: 'Vetted', basis: 'Graduated from a personal skill: passed the run-and-refine vetting loop + a champion sign-off.' };
  return { key: 'vetted', label: 'Vetted', basis: 'Cleared the publish checklist: complete listing + a defined access level.' };
}

// Provenance line: who is behind it, who it is for, when it landed. A skill we
// publish names us, not the demo person who happened to publish it.
function provenanceLine(s: MarketSkill): string {
  let who = s.author_email || s.created_by || 'your team';
  let scope = 'whole company';
  if ((s.scope || 'company') === 'team') scope = `${s.department || 'one'} team`;
  if (s.source === 'provider') {
    who = 'Agents Anywhere';
    scope = 'every company';
  }
  const added = s.created_at ? new Date(s.created_at).toLocaleDateString() : null;
  if (added) return `${who} · ${scope} · added ${added}`;
  return `${who} · ${scope}`;
}

/*
 * Three groups, because a person choosing something wants to know who stands
 * behind it before they read what it does. One flat grid made a skill we
 * publish and a draft somebody in your team wrote look like the same kind of
 * offer.
 *
 * A group with nothing in it is not drawn. An empty "from Agents Anywhere"
 * heading on a company that has none teaches people the catalogue is empty
 * rather than that it has not arrived.
 */
const MARKET_GROUPS: Array<{ key: string; title: string; note: string }> = [
  { key: 'provider', title: 'From Agents Anywhere', note: 'Built by us and kept working for every company.' },
  { key: 'company', title: 'From your company', note: 'Published by your own people, for your own people.' },
];

function groupOf(s: MarketSkill): string {
  if (s.source === 'provider') return 'provider';
  return 'company';
}

// ── tabs + vetting framing ──────────────────────────────────────────────────

/* One view, not three.
 *
 * Vetting pipeline and Provenance were tabs over the same rows this page
 * already lists, sliced by where a skill is in its own process and by who
 * wrote it. Both are the author's business and the vetting log's, and neither
 * is what somebody opens Skills & Plugins to find out. What is left is the
 * catalogue: what exists, where it came from, and whether you can run it.
 *
 * Both views are gone rather than hidden behind a flag: they were a second and
 * third reading of rows this page already draws, and a view nobody opens is a
 * view nobody keeps true. Git has them if the vetting log ever wants one. */
const TABS = [
  { key: 'marketplace', label: 'Marketplace' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

// One tab left, so the row is just the count it always carried on the right.
const TabBar: React.FC<{ tab: TabKey; onTab: (t: TabKey) => void; count: number }> = ({ count }) => (
  <div className="flex items-center gap-2 mb-4 flex-wrap">
    {TABS.length > 1 && null}
    <div className="flex-1" />
    <span className="font-sans text-[10.5px] uppercase tracking-wide text-ink-soft">{count} skill{count === 1 ? '' : 's'}</span>
  </div>
);

// Honest re-frame of the design's "every skill is vetted before it lands" card —
// describes the REAL gate (publish checklist + graduation vetting loop), not the
// aspirational static-scan / prompt-injection copy we don't yet run.
const VettingBanner: React.FC<{ canManage: boolean }> = ({ canManage }) => (
  <div className="bg-ink text-white rounded-card p-4 flex items-start gap-3.5 mb-4">
    <span className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 grid place-items-center shrink-0"><ShieldCheck size={18} /></span>
    <div className="flex-1">
      <div className="font-semibold text-[13.5px]">Ready to use.</div>
      <div className="text-[12.5px] text-white/65 leading-snug">
        Only skills your Owner, your AI Champion or Agents Anywhere have turned on are listed here.{' '}
        {canManage ? 'Drafts stay with their editors until they are published.' : 'Open one to read what it does, then run it.'}
      </div>
    </div>
  </div>
);

// Champion/trainer queue — skills a participant chose to SHARE. Effectiveness is
// visible here (and only here) because the participant proposed graduation. Self-
// hides for non-managers (403) and when there's nothing to review.
// Champion/trainer queue — open ACCESS requests across every restricted skill.
// The counterpart of the "Access request" notification: land on /aai/skills and
// the ask is right here, not hidden behind each card's access icon. Self-hides
// for non-managers (403) and when nothing is pending.
type PendingAccess = { skill_id: string; skill_name: string; member_email: string; requested_at: string | null };
export const AccessRequestsQueue: React.FC<{ onDecided: () => void }> = ({ onDecided }) => {
  const [items, setItems] = useState<PendingAccess[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { const { data } = await aaiApi.pendingSkillAccess(); setItems(data.requests || []); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const decide = async (r: PendingAccess, grant: boolean) => {
    setBusy(`${r.skill_id}:${r.member_email}`);
    try { await aaiApi.decideSkillAccess(r.skill_id, r.member_email, grant); await load(); onDecided(); }
    finally { setBusy(null); }
  };
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-brand-100 rounded-card p-4 mb-4">
      <div className="flex items-center gap-2 mb-1"><Lock size={16} className="text-brand-500" />
        <span className="text-[13px] font-semibold text-ink">Access requests ({items.length})</span></div>
      <p className="text-[12px] text-ink-dim mb-3">Teammates asking to use a restricted skill — grant or deny each one.</p>
      <div className="space-y-1.5">
        {items.map((r) => {
          const k = `${r.skill_id}:${r.member_email}`;
          return (
            <div key={k} className="flex items-center gap-2 bg-canvas rounded-lg px-3 py-2 text-[12.5px] flex-wrap">
              <span className="font-semibold text-ink truncate">{r.skill_name}</span>
              <span className="text-ink-dim flex-1 min-w-0 truncate">{r.member_email}</span>
              {r.requested_at && <span className="text-[11px] text-ink-soft shrink-0">{new Date(r.requested_at).toLocaleDateString()}</span>}
              <button onClick={() => decide(r, true)} disabled={busy === k}
                className="text-[11.5px] font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50">Grant</button>
              <button onClick={() => decide(r, false)} disabled={busy === k}
                className="text-[11.5px] font-semibold text-ink-soft hover:text-red-500 disabled:opacity-50">Deny</button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GraduationQueue: React.FC = () => {
  const [items, setItems] = useState<Graduation[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { const { data } = await aaiApi.getGraduationQueue(); setItems(data.graduations || []); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const decide = async (g: Graduation, approve: boolean) => {
    if (!approve) {
      const note = window.prompt('Why not share this skill? The person who offered it reads this.');
      if (note === null) return;
      if (!note.trim()) {
        toast.error('Say why, in a sentence. A decline always carries a reason.');
        return;
      }
      setBusy(g.id);
      try { await aaiApi.decideGraduation(g.id, false, note.trim()); await load(); }
      finally { setBusy(null); }
      return;
    }
    setBusy(g.id);
    try { await aaiApi.decideGraduation(g.id, approve); await load(); }
    finally { setBusy(null); }
  };
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-brand-100 rounded-card p-4 mb-4">
      <div className="flex items-center gap-2 mb-1"><Share2 size={16} className="text-brand-500" />
        <span className="text-[13px] font-semibold text-ink">Skills proposed for sharing ({items.length})</span></div>
      <p className="text-[12px] text-ink-dim mb-3">A team member chose to share these vetted skills. Their effectiveness is shown so you can decide.</p>
      <div className="space-y-2">
        {items.map((g) => {
          const e = g.effectiveness;
          return (
            <div key={g.id} className="bg-canvas rounded-lg px-3.5 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-ink">{g.skill_name}</span>
                <span className="text-[11.5px] text-ink-soft">by {g.proposed_by} → {g.to_scope}{g.department ? ` · ${g.department}` : ''}</span>
              </div>
              {e && (
                <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px]">
                  {e.vetted && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-semibold">vetted ✓</span>}
                  <span className="rounded-full bg-canvas-inset text-ink-muted px-2 py-0.5">{e.success_rate != null ? `${Math.round(e.success_rate * 100)}% worked` : 'no runs'} · {e.runs} runs</span>
                  <span className="rounded-full bg-canvas-inset text-ink-muted px-2 py-0.5">v{e.versions}</span>
                  {e.adoption > 0 && <span className="rounded-full bg-canvas-inset text-ink-muted px-2 py-0.5">{e.adoption} using</span>}
                </div>
              )}
              {/* SKL-9: the run record itself, failures included — "used 14 times
                  over 3 weeks, 12 worked" beats "I'd like to share this". */}
              {(g.evidence?.recent_runs?.length ?? 0) > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {g.evidence!.recent_runs.slice(0, 5).map((r, i) => (
                    <p key={i} className="text-[11px] text-ink-dim truncate">
                      <span className={r.outcome === 'worked' ? 'text-emerald-600' : r.outcome === 'partial' ? 'text-amber-600' : 'text-red-500'}>
                        {r.outcome}
                      </span>
                      {r.at ? ` · ${new Date(r.at).toLocaleDateString()}` : ''}{r.note ? ` — ${r.note}` : ''}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => decide(g, true)} disabled={busy === g.id}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12px] font-semibold px-3 py-1.5 whitespace-nowrap">
                  {busy === g.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} />} Approve
                </button>
                <button onClick={() => decide(g, false)} disabled={busy === g.id}
                  className="inline-flex items-center gap-1 rounded-full border border-line text-ink-muted hover:bg-canvas disabled:opacity-50 text-[12px] font-semibold px-3 py-1.5 whitespace-nowrap">
                  <X size={13} /> Decline
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/*
 * I1. Only one direction of the loop was joined up: the Requests page points
 * people at the marketplace when the thing they want may already exist. Coming
 * the other way there was no search at all, so someone who could not find what
 * they needed had nowhere to go from here.
 *
 * Close matches come first, because reusing a skill is always cheaper than
 * building one. The request option appears only when the search finds nothing
 * worth reusing, and it carries the words the person typed, so they are not
 * asked to describe it a second time.
 */
export const MarketSearch: React.FC<{
  value: string;
  onChange: (v: string) => void;
  matched: number;
  hideInput?: boolean;
}> = ({ value, onChange, matched, hideInput }) => {
  const { go } = useFeatureNav();
  const asked = value.trim();
  return (
    <div className="mb-3">
      {!hideInput && <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="What are you trying to do?"
          aria-label="Search skills"
          className="w-full rounded-lg border border-line pl-9 pr-3 py-2 text-[13px] focus:border-ink focus:outline-none"
        />
      </div>}
      {asked.length >= 2 && matched === 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-brand-100 bg-brand-50 px-3 py-2.5">
          <span className="text-[12.5px] text-ink">
            Nobody has built this yet.
          </span>
          <button
            onClick={() => go(`/requests?title=${encodeURIComponent(asked)}`)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-brand-600 whitespace-nowrap"
          >
            <Send size={13} /> Ask for it
          </button>
        </div>
      )}
    </div>
  );
};

const SkillsMarketplacePage: React.FC = () => {
  const [skills, setSkills] = useState<AaiSkill[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AaiSkill | null>(null);
  const [editing, setEditing] = useState<AaiSkill | 'new' | null>(null);
  const [accessFor, setAccessFor] = useState<AaiSkill | null>(null);
  const [tab, setTab] = useState<TabKey>('marketplace');
  const [query, setQuery] = useState('');

  // Matched on the two things a person searches by: what it is called, and what
  // it is for. Substring rather than a scored search: the catalogue is a
  // company's own skills, not a web index.
  const needle = query.trim().toLowerCase();
  const visible = needle.length < 2
    ? skills
    : skills.filter((s) => `${s.name} ${s.problem || ''}`.toLowerCase().includes(needle));


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await aaiApi.listSkills();
      setSkills(data.skills); setCanManage(data.can_manage); setError(null);
    } catch { setError('Could not load the marketplace.'); }
    finally { setLoading(false); }
  }, []);

  // The caller's own skills are a separate read on purpose: they are self-only
  // and never published, so they cannot come from the marketplace query. Losing
  // them must not take the marketplace with it, hence the quiet catch.
  const [mine, setMine] = useState<PersonalSkill[]>([]);
  useEffect(() => {
    let live = true;
    aaiApi.getMySkills()
      .then((res) => { if (live) setMine(res.data.skills || []); })
      .catch(() => { if (live) setMine([]); });
    return () => { live = false; };
  }, []);
  useEffect(() => { load(); }, [load]);

  // One card factory keeps create/edit/publish/access/request behavior identical
  // across the Marketplace grid and the Vetting-pipeline groups.
  const renderCard = (s: AaiSkill) => (
    <SkillCard key={s.id} skill={s} canManage={canManage}
      onOpen={() => setDetail(s)} onAccess={() => setAccessFor(s)}
      onEdit={() => setEditing(s)} onRefresh={load} />
  );

  const empty = (
    <div className="bg-white border border-line-soft rounded-card p-8 text-center text-[13px] text-ink-soft">
      {canManage ? 'No skills yet — publish your first proven process.' : 'No skills published yet — check back soon.'}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title="Skills & plugins marketplace"
        subtitle="Things your colleagues have already worked out how to do with AI. Find one, ask for access if it is restricted, and run it."
      />

      <GraduationQueue />
      <AccessRequestsQueue onDecided={load} />

      <TabBar tab={tab} onTab={setTab} count={skills.length} />

      {canManage && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-2 whitespace-nowrap"><Plus size={14} /> New skill</button>
        </div>
      )}

      {loading ? <Loading /> : error ? <ErrorNote message={error} /> : skills.length === 0 ? empty : (
        <>
          {tab === 'marketplace' && (
            <>
              <VettingBanner canManage={canManage} />
              <MarketSearch value={query} onChange={setQuery} matched={visible.length} />
              {mine.length > 0 && (
                <div className="mb-5">
                  <div className="mb-2">
                    <div className="text-[13px] font-semibold text-ink">Your own</div>
                    <div className="text-[12px] text-ink-dim">
                      Yours alone until you propose one for the company. Nobody else can see these.
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mine.map((m) => (
                      <div key={m.id} className="bg-white border border-line-soft rounded-card p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="font-semibold text-[14px] leading-snug">{m.name}</span>
                          <span className="inline-block rounded-full bg-canvas-inset text-ink-muted px-2 py-0.5 text-[10px] font-semibold shrink-0">Private</span>
                        </div>
                        <p className="m-0 text-[12.5px] text-ink-dim leading-snug">
                          {m.problem || <span className="italic text-ink-soft">No description yet</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {MARKET_GROUPS.map((g) => {
                const rows = (visible as MarketSkill[]).filter((s) => groupOf(s) === g.key);
                if (rows.length === 0) return null;
                return (
                  <div key={g.key} className="mb-5">
                    <div className="mb-2">
                      <div className="text-[13px] font-semibold text-ink">{g.title}</div>
                      <div className="text-[12px] text-ink-dim">{g.note}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{rows.map(renderCard)}</div>
                  </div>
                );
              })}
            </>
          )}

        </>
      )}

      {detail && <DetailModal skill={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {editing && <EditModal skill={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {accessFor && <AccessModal skill={accessFor} onClose={() => setAccessFor(null)} />}
    </div>
  );
};

const SkillCard: React.FC<{ skill: AaiSkill; canManage: boolean; onOpen: () => void; onAccess: () => void; onEdit: () => void; onRefresh: () => void }> = ({ skill, canManage, onOpen, onAccess, onEdit, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const access = skill.my_access || 'none';
  const usable = access === 'open' || access === 'granted';
  // A skill we publish belongs to us, not to this company, so the backend
  // refuses to publish, edit or re-scope it from here. Offering the buttons
  // anyway meant controls that looked live and did nothing at all.
  const theirs = skill.source === 'provider';
  const manageable = canManage && !theirs;

  const request = async () => { setBusy(true); try { await aaiApi.requestSkillAccess(skill.id); await onRefresh(); } finally { setBusy(false); } };
  const togglePublish = async () => { setBusy(true); try { await aaiApi.publishSkill(skill.id, !skill.published); await onRefresh(); } catch (e) { alert((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Could not publish.'); } finally { setBusy(false); } };
  const vet = vetState(skill as MarketSkill);

  return (
    <div className="bg-white border border-line-soft rounded-card p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-1">
        <button onClick={onOpen} className="font-semibold text-[14px] text-left hover:text-brand-600 leading-snug">{skill.name}</button>
        <div className="flex items-center gap-1.5 shrink-0">
          {skill.shape === 'plugin' && (
            <span className="inline-block rounded-full bg-purple-50 text-purple-700 px-2 py-0.5 text-[10px] font-semibold">Plugin</span>
          )}
          <span title={vet.basis} className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${VET_BADGE[vet.key]}`}>{vet.label}</span>
          {!theirs && (
            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACCESS_BADGE[skill.access_level]}`}>{skill.access_level}</span>
          )}
          {theirs && (
            <span className="inline-block rounded-full bg-canvas-inset px-2 py-0.5 text-[10px] font-semibold text-ink-muted">open to everyone</span>
          )}
        </div>
      </div>
      <div className="text-[11px] text-ink-soft mb-2">{provenanceLine(skill as MarketSkill)}</div>
      <p className="text-[12.5px] text-ink-dim leading-snug flex-1">{skill.problem || <span className="italic text-ink-soft">No description yet</span>}</p>

      {/* What it needs connected. A plugin says so even when the list is empty,
          because a plugin without a connection is the thing to notice; a plain
          skill with no tools is simply a skill and says nothing. */}
      {(skill.tools || []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-ink-soft">Needs</span>
          {(skill.tools || []).map((t) => (
            <span key={t} className="rounded-full bg-canvas-inset text-ink-muted px-2 py-0.5 text-[10.5px] font-semibold capitalize">{t}</span>
          ))}
        </div>
      )}
      {skill.shape === 'plugin' && (skill.tools || []).length === 0 && (
        <p className="mt-2 m-0 text-[11px] text-amber-600">No connection recorded yet, so this cannot run.</p>
      )}

      <div className="flex items-center gap-2 mt-3">
        {usable ? (
          <button onClick={onOpen} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-1.5 whitespace-nowrap"><Play size={13} /> Open</button>
        ) : access === 'requested' ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-600"><Clock size={13} /> Pending approval</span>
        ) : (
          <button disabled={busy} onClick={request} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-600 border border-brand-200 rounded-lg px-3 py-1.5 disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Request access</button>
        )}
        {manageable && (
          <div className="ml-auto flex items-center gap-2">
            <button onClick={togglePublish} disabled={busy} className={`text-[11.5px] font-semibold ${skill.published ? 'text-ink-dim hover:text-ink' : 'text-emerald-600 hover:text-emerald-700'} disabled:opacity-50`}>{skill.published ? 'Unpublish' : 'Publish'}</button>
            <button onClick={onAccess} title="Access & usage" className="text-ink-soft hover:text-brand-600"><Users size={14} /></button>
            <button onClick={onEdit} className="text-[11.5px] font-semibold text-ink-dim hover:text-brand-600">Edit</button>
          </div>
        )}
      </div>
      {manageable && !skill.published && <span className="mt-2 text-[10.5px] font-semibold text-amber-600">Draft · only editors see this</span>}
    </div>
  );
};

// ── modals ────────────────────────────────────────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
    <div className={`bg-white rounded-card p-5 w-full ${wide ? 'max-w-lg' : 'max-w-md'} shadow-xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <button onClick={onClose} className="text-ink-soft hover:text-ink-muted"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

// The run brief Orbit receives: the skill as instructions plus how to drive
// the session. Framed as a RUN so the problem-scoping opener doesn't hijack it.
const runPrompt = (s: AaiSkill): string => {
  const lines: string[] = [];
  lines.push('I want to run one of our marketplace skills step by step — this is a run, not a new build to scope.');
  lines.push('');
  lines.push(`Skill: ${s.name}`);
  if (s.problem) lines.push(s.problem);
  if ((s.process_steps || []).length > 0) {
    lines.push('');
    lines.push('Process:');
    s.process_steps.forEach((st, i) => lines.push(`${i + 1}. ${st}`));
  }
  if (s.example_input) {
    lines.push('');
    lines.push(`Example input:\n${s.example_input}`);
  }
  if (s.example_output) {
    lines.push('');
    lines.push(`Example output:\n${s.example_output}`);
  }
  lines.push('');
  lines.push('Ask me for the input the first step needs, then work through the steps with me and show me the result. If any step sends something, nothing goes out without my explicit approval.');
  return lines.join('\n');
};

const DetailModal: React.FC<{ skill: AaiSkill; onClose: () => void; onChanged: () => void }> = ({ skill, onClose, onChanged }) => {
  const [full, setFull] = useState<AaiSkill>(skill);
  const [used, setUsed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { aaiApi.getSkill(skill.id).then((r) => setFull(r.data)).catch(() => undefined); }, [skill.id]);
  const usable = full.my_access === 'open' || full.my_access === 'granted';

  const [running, setRunning] = useState(false);
  const logUse = async () => {
    try { await aaiApi.useSkill(full.id); setUsed(true); onChanged(); }
    catch { /* the run still proceeds — logging is bookkeeping */ }
  };
  // Run the skill IN the app: the dedicated runner chat. The skill's steps and
  // the caller's delegation posture are injected server-side per turn — never
  // the Orbit scoping drawer (its problem lane belongs to the build-scoping
  // plugin and treats anything seeded as a build spec).
  const run = async () => {
    await logUse();
    setRunning(true);
  };
  // A rides along: the same brief to the clipboard, for any external AI.
  const copyPrompt = async () => {
    setBusy(true);
    try {
      await navigator.clipboard.writeText(runPrompt(full));
      setCopied(true);
      await logUse();
    } catch { alert('Could not copy — select the steps manually.'); }
    finally { setBusy(false); }
  };

  if (running) {
    return <RunSkillModal skill={full} onBack={() => setRunning(false)} onClose={onClose} />;
  }
  return (
    <Modal title={full.name} onClose={onClose} wide>
      {full.problem && <p className="text-[13px] text-ink mb-3">{full.problem}</p>}
      {full.use_case_title && <p className="text-[11.5px] text-ink-soft mb-3">Solves use case: <span className="font-semibold text-ink-muted">{full.use_case_title}</span></p>}
      {full.process_steps.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] font-sans uppercase tracking-wide text-ink-soft mb-1.5">Process</div>
          <ol className="list-decimal ml-5 space-y-1 text-[12.5px] text-ink">{full.process_steps.map((st, i) => <li key={i}>{st}</li>)}</ol>
        </div>
      )}
      {(full.example_input || full.example_output) && (
        <div className="grid grid-cols-1 gap-2 mb-3">
          {full.example_input && <div><div className="text-[11px] font-sans uppercase tracking-wide text-ink-soft mb-1">Example input</div><pre className="bg-canvas rounded-lg p-2.5 text-[12px] whitespace-pre-wrap">{full.example_input}</pre></div>}
          {full.example_output && <div><div className="text-[11px] font-sans uppercase tracking-wide text-ink-soft mb-1">Example output</div><pre className="bg-canvas rounded-lg p-2.5 text-[12px] whitespace-pre-wrap">{full.example_output}</pre></div>}
        </div>
      )}
      {usable && (
        <div className="flex items-center gap-2 flex-wrap">
          <button disabled={busy} onClick={run} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 disabled:opacity-50 whitespace-nowrap">
            {busy && <Loader2 size={15} className="animate-spin" />}
            {!busy && <Play size={15} />} Run this skill
          </button>
          <button disabled={busy} onClick={copyPrompt} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-muted border border-line hover:border-brand-300 rounded-lg px-3 py-2 disabled:opacity-50 whitespace-nowrap">
            {copied && <Check size={14} className="text-emerald-500" />}
            {!copied && <Copy size={14} />}
            {copied && 'Copied — logged as a use'}
            {!copied && 'Copy as prompt'}
          </button>
          {used && !copied && <span className="text-[11.5px] text-ink-soft">Logged as a use.</span>}
        </div>
      )}
      {!usable && (
        <p className="text-[12.5px] text-ink-soft">Request access to use this skill.</p>
      )}
    </Modal>
  );
};

const EditModal: React.FC<{ skill: AaiSkill | null; onClose: () => void; onSaved: () => void }> = ({ skill, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: skill?.name || '',
    problem: skill?.problem || '',
    steps: (skill?.process_steps || []).join('\n'),
    example_input: skill?.example_input || '',
    example_output: skill?.example_output || '',
    access_level: skill?.access_level || 'request',
    use_case_id: skill?.use_case_id || '',
    // What the thing IS. A plugin carries its own connection, which is its
    // identity rather than a detail of how it runs, so the card badges it and
    // says when that connection is missing.
    kind: skill?.kind || 'context',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // What this skill needs connected to run. A requirement, never a permission:
  // saying it needs Gmail grants nobody access to anyone's mail. It is what
  // makes "what breaks if I switch this off" answerable, and what lets a build
  // stop and ask before it fails halfway through.
  const [tools, setTools] = useState((skill?.tools || []).join(', '));
  // Approved requests this skill can be linked to — publishing a linked skill
  // delivers it to the requester (auto-access + "your tool is ready" ping).
  const [approvedRequests, setApprovedRequests] = useState<AaiRequest[]>([]);
  useEffect(() => {
    aaiApi.listRequests()
      .then(({ data }) => setApprovedRequests(data.requests.filter((r: AaiRequest) => r.status === 'approved')))
      .catch(() => setApprovedRequests([]));
  }, []);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { setErr('A name is required.'); return; }
    setBusy(true); setErr(null);
    const body = {
      name: f.name.trim(), problem: f.problem || undefined,
      process_steps: f.steps.split('\n').map((s) => s.trim()).filter(Boolean),
      example_input: f.example_input || undefined, example_output: f.example_output || undefined,
      access_level: f.access_level,
      use_case_id: f.use_case_id || undefined,
      kind: f.kind,
    };
    const wanted = tools.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      const saved = skill ? (await aaiApi.updateSkill(skill.id, body)).data : (await aaiApi.createSkill(body)).data;
      // Saved separately because the tools are their own record, not a column
      // on the skill. A failure here must not lose the edit the person made.
      try { await aaiApi.setSkillTools(saved.id, wanted); } catch { /* the skill is saved either way */ }
      onSaved();
    } catch (e) { setErr((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Could not save.'); setBusy(false); }
  };

  return (
    <Modal title={skill ? 'Edit skill' : 'New skill'} onClose={onClose} wide>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Name</span>
        <input autoFocus value={f.name} onChange={(e) => set('name', e.target.value)} className={inputCls} /></label>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Problem it solves</span>
        <textarea value={f.problem} onChange={(e) => set('problem', e.target.value)} rows={2} className={inputCls} /></label>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Process steps <span className="text-ink-soft font-normal">· one per line</span></span>
        <textarea value={f.steps} onChange={(e) => set('steps', e.target.value)} rows={4} placeholder={'Paste the request\nReview the draft\nSend'} className={inputCls} /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Example input</span>
          <textarea value={f.example_input} onChange={(e) => set('example_input', e.target.value)} rows={2} className={inputCls} /></label>
        <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Example output</span>
          <textarea value={f.example_output} onChange={(e) => set('example_output', e.target.value)} rows={2} className={inputCls} /></label>
      </div>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">What this is</span>
        <select value={f.kind} onChange={(e) => set('kind', e.target.value)} className={inputCls}>
          <option value="context">Skill — something the assistant can do on ask</option>
          <option value="workflow">Skill — a reusable, repeatable setup</option>
          <option value="plugin">Plugin — it carries its own connection</option>
        </select>
        {f.kind === 'plugin' && (
          <span className="mt-1 block text-[11px] text-ink-soft">
            A plugin is named by the tool it works in, so name that tool below. Without one it will
            say on its card that it cannot run.
          </span>
        )}</label>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Access</span>
        <select value={f.access_level} onChange={(e) => set('access_level', e.target.value)} className={inputCls}>
          <option value="open">Open — anyone can use it</option>
          <option value="request">Request — approve each person</option>
          <option value="restricted">Restricted — invite only</option>
        </select></label>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Tools it needs <span className="text-ink-soft font-normal">· optional — comma separated, e.g. gmail, slack</span></span>
        <input value={tools} onChange={(e) => setTools(e.target.value)} placeholder="gmail, slack" className={inputCls} />
        <span className="mt-1 block text-[11px] text-ink-soft">
          Naming a tool here does not grant access to anyone&apos;s account. It lets people see what
          breaks before switching a tool off, and lets a build ask for a missing connection up front.
        </span>
        {f.kind === 'plugin' && tools.trim() === '' && (
          <span className="mt-1 block text-[11px] font-semibold text-amber-600">
            No connection named yet, so this plugin cannot run.
          </span>
        )}</label>
      <label className="block mb-3"><span className="block text-[11.5px] font-semibold text-ink mb-1">Built from request <span className="text-ink-soft font-normal">· optional — publishing delivers it to the requester</span></span>
        <select value={f.use_case_id} onChange={(e) => set('use_case_id', e.target.value)} className={inputCls}>
          <option value="">Not linked to a request</option>
          {approvedRequests.map((r) => (
            <option key={r.id} value={r.id}>{r.title} — {r.requester_email}</option>
          ))}
        </select></label>
      {err && <p className="text-[12px] text-red-600 mb-2">{err}</p>}
      <div className="flex justify-between items-center mt-1">
        {skill ? <button onClick={async () => { if (window.confirm('Delete this skill?')) { await aaiApi.deleteSkill(skill.id); onSaved(); } }} className="inline-flex items-center gap-1 text-[12px] font-semibold text-red-500 hover:text-red-600"><Trash2 size={13} /> Delete</button> : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-canvas-inset text-ink">Cancel</button>
          <button disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 text-white disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button>
        </div>
      </div>
    </Modal>
  );
};

const AccessModal: React.FC<{ skill: AaiSkill; onClose: () => void }> = ({ skill, onClose }) => {
  const [rows, setRows] = useState<SkillAccessRow[]>([]);
  const [usage, setUsage] = useState<SkillUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([aaiApi.skillAccessQueue(skill.id), aaiApi.skillUsage(skill.id)]);
      setRows(a.data.access); setUsage(u.data);
    } finally { setLoading(false); }
  }, [skill.id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (email: string, grant: boolean) => { await aaiApi.decideSkillAccess(skill.id, email, grant); await load(); };

  return (
    <Modal title={`Access & usage · ${skill.name}`} onClose={onClose} wide>
      {usage && (
        <div className="flex items-center gap-4 mb-4 text-[12.5px]">
          <span className="inline-flex items-center gap-1.5 text-ink"><BarChart3 size={14} className="text-brand-500" /> <b>{usage.total_uses}</b> uses</span>
          <span className="text-ink-dim">{usage.unique_users} people</span>
          {usage.last_used && <span className="text-ink-soft">last {usage.last_used.slice(0, 10)}</span>}
        </div>
      )}
      {loading ? <Loading /> : (
        <div className="space-y-1.5">
          {rows.length === 0 && <p className="text-[12.5px] text-ink-soft">No access requests yet.</p>}
          {rows.map((r) => (
            <div key={r.member_email} className="flex items-center gap-2 bg-canvas rounded-lg px-3 py-2 text-[12.5px]">
              <span className="flex-1 truncate">{r.member_email}</span>
              <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${r.status === 'granted' ? 'bg-emerald-50 text-emerald-700' : r.status === 'denied' ? 'bg-canvas-inset text-ink-dim' : 'bg-amber-50 text-amber-700'}`}>{r.status}</span>
              {r.status !== 'granted' && <button onClick={() => decide(r.member_email, true)} className="text-[11.5px] font-semibold text-emerald-600">Grant</button>}
              {r.status !== 'denied' && <button onClick={() => decide(r.member_email, false)} className="text-[11.5px] font-semibold text-ink-soft hover:text-red-500">Deny</button>}
            </div>
          ))}
        </div>
      )}
      {usage?.by_member && usage.by_member.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-sans uppercase tracking-wide text-ink-soft mb-1.5">Usage by member</div>
          <div className="space-y-1">
            {usage.by_member.map((m) => (
              <div key={m.member_email} className="flex items-center gap-2 text-[12px] text-ink-muted">
                <span className="flex-1 truncate">{m.member_email}</span><span className="font-semibold">{m.uses}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SkillsMarketplacePage;
